import { useEffect, useState, useCallback } from 'react';
import { Typography, Box, CircularProgress, Alert } from '@mui/material';
import DateRangeSelector from '../../components/DateRangeSelector';
import { PAGE_CARD_SX } from '../../theme/pageCardSx';
import NewsMagazineStatsTable from '../../components/naver/table/NewsMagazineStatsTable';
import PublisherFilter from '../../components/naver/PublisherFilter';
import apiClient from '../../utils/axiosClient';
import dayjs, { Dayjs } from 'dayjs';
import { NewsMagazineStats } from '../../types/naver.types';

export default function NewsMagazineStatsPage() {
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [data, setData] = useState<NewsMagazineStats[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [selectedPublisher, setSelectedPublisher] = useState<string | undefined>(undefined);

  const fetchData = useCallback(() => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setErrorMessage('');
    apiClient
      .get('/api/v1/stat/news/magazine/stats', {
        params: {
          start_date: startDate.format('YYYY-MM-DD'),
          end_date: endDate.format('YYYY-MM-DD'),
          ...(selectedPublisher ? { publisher: selectedPublisher } : {}),
        },
      })
      .then((res) => setData(res.data))
      .catch(() => setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.'))
      .finally(() => setLoading(false));
  }, [startDate, endDate, selectedPublisher]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Box sx={{ opacity: loading ? 0.4 : 1, transition: 'opacity 0.3s ease', minHeight: '100vh' }}>
      <Box display="flex" alignItems="center">
        <DateRangeSelector
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onSearch={fetchData}
        />
      </Box>

      <Box
        sx={PAGE_CARD_SX}
      >
        <PublisherFilter selected={selectedPublisher} onSelect={setSelectedPublisher} />

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <CircularProgress size={50} sx={{ color: '#0044D2' }} />
            <Typography variant="body1" sx={{ ml: 2 }}>
              데이터 로딩 중...
            </Typography>
          </Box>
        ) : errorMessage ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        ) : data && startDate && endDate && (
          <NewsMagazineStatsTable data={data} startDate={startDate} endDate={endDate} />
        )}
      </Box>
    </Box>
  );
}
